import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';
import { newsTimelineData } from '@/mocks/newsTimeline';

const categories = ['全部', 'XR设备', '6G网络', '脑机接口', '量子计算', 'AI', '元宇宙平台', '区块链', '政策', '人形机器人'];

const categoryColors: Record<string, string> = {
  'XR设备': '#FF6B35',
  '6G网络': '#00FF88',
  '脑机接口': '#A855F7',
  '量子计算': '#00FFCC',
  'AI': '#FFD700',
  '元宇宙平台': '#FF4081',
  '区块链': '#4FC3F7',
  '政策': '#81C784',
  '人形机器人': '#FF8A65',
  'Web5.0': '#00FFCC',
  'Web3.0': '#4FC3F7',
  '裸眼3D': '#FF6B35',
  '数字孪生': '#00FF88',
  '量子通信': '#00FFCC',
  '量子安全': '#00FFCC',
  'AIGC': '#FFD700',
  '教育': '#81C784',
  '芯片': '#FF8A65',
  '市场报告': '#4FC3F7',
  '卫星互联网': '#00FF88',
  '智慧城市': '#81C784',
  '产业联盟': '#FF4081',
  '全球治理': '#4FC3F7',
  '医疗元宇宙': '#FF4081',
  '游戏元宇宙': '#FF6B35',
  'Web3.0游戏': '#4FC3F7',
  'IP元宇宙': '#FFD700',
  '元宇宙活动': '#FF4081',
  '元宇宙外交': '#81C784',
  '教育元宇宙': '#81C784',
  '算力基础设施': '#FF8A65',
  '政策法规': '#81C784',
  '标准': '#81C784',
  '6G设备': '#00FF88',
  'AI+量子': '#A855F7',
  'AI+区块链': '#A855F7',
};

const categoryIcons: Record<string, string> = {
  'XR设备': 'ri-glasses-line',
  '6G网络': 'ri-signal-tower-line',
  '脑机接口': 'ri-mental-health-line',
  '量子计算': 'ri-bubble-chart-line',
  'AI': 'ri-brain-line',
  '元宇宙平台': 'ri-global-line',
  '区块链': 'ri-links-line',
  '政策': 'ri-government-line',
  '人形机器人': 'ri-robot-line',
};

const sourceFlags: Record<string, string> = {
  '新华社': '🇨🇳', '人民日报': '🇨🇳', '科技日报': '🇨🇳',
  'MIT Technology Review': '🇺🇸', 'The Verge': '🇺🇸', 'TechCrunch': '🇺🇸',
  'Reuters': '🌐', 'Bloomberg': '🇺🇸', 'Wired': '🇺🇸', 'Nature': '🌐',
};

// Featured/hot news items for the top showcase
const featuredNews = [
  {
    id: 'f1',
    category: 'AI',
    title: 'OpenAI GPT-6震撼发布：多模态能力全面超越人类专家，AGI时代正式开启',
    summary: '在医学、法律、科学研究等专业领域全面超越人类顶尖专家，具备完整的视觉、听觉、触觉多模态理解能力。业界认为这是AGI时代的正式开启，将彻底重塑人类工作与创造力边界。',
    date: '2025-12-18',
    source: 'OpenAI Blog',
    impact: '颠覆性',
    impactColor: '#FFD700',
    img: 'https://public.readdy.ai/ai/img_res/3fef1016dc3845e38b6f4658214347e0.jpg',
    tags: ['AI', 'AGI', 'GPT-6'],
    readTime: '8分钟',
  },
  {
    id: 'f2',
    category: '6G网络',
    title: '中国6G标准正式发布，深圳成全球首个6G商用城市，峰值速率1Tbps',
    summary: '工业和信息化部正式发布中国6G技术标准，深圳成为全球首个6G商用城市。6G网络峰值速率达1Tbps，端到端延迟低于0.1ms，为元宇宙大规模商用奠定基础设施底座。',
    date: '2026-03-15',
    source: '新华社',
    impact: '里程碑',
    impactColor: '#00FF88',
    img: 'https://readdy.ai/api/search-image?query=6G%20network%20commercial%20launch%20Shenzhen%20China%20terahertz%20signal%20tower%20glowing%20green%20neon%20futuristic%20city%20dark%20background%20cinematic&width=600&height=340&seq=news-feat-2&orientation=landscape',
    tags: ['6G', '中国', '深圳'],
    readTime: '6分钟',
  },
  {
    id: 'f3',
    category: '脑机接口',
    title: 'Neuralink完成第100例人体植入，意念打字突破200词/分钟，消费级产品2027年上市',
    summary: '最新数据显示患者意念打字速度突破每分钟200词，超越普通人键盘打字速度。马斯克宣布2027年将推出消费级产品，定价低于5000美元，脑机接口大众化时代即将到来。',
    date: '2026-03-02',
    source: 'MIT Technology Review',
    impact: '突破性',
    impactColor: '#A855F7',
    img: 'https://readdy.ai/api/search-image?query=Neuralink%20brain%20computer%20interface%20neural%20link%20human%20mind%20digital%20connection%20purple%20neon%20glow%20futuristic%20medical%20technology%20dark%20background%20cinematic&width=600&height=340&seq=news-feat-3&orientation=landscape',
    tags: ['脑机接口', 'Neuralink', 'BCI'],
    readTime: '5分钟',
  },
];

export default function NewsTimeline() {
  const [activeCategory, setActiveCategory] = useState('全部');
  const [showCount, setShowCount] = useState(9);
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const [activeFeatured, setActiveFeatured] = useState(0);
  const { ref: headerRef, isVisible: headerVisible } = useScrollAnimation();
  const { ref: featuredRef, isVisible: featuredVisible } = useScrollAnimation({ threshold: 0.05 });
  const { ref: gridRef, isVisible: gridVisible } = useScrollAnimation({ threshold: 0.05 });

  const filtered = activeCategory === '全部'
    ? newsTimelineData
    : newsTimelineData.filter((n) => n.category === activeCategory || n.tags.includes(activeCategory));

  const displayed = filtered.slice(0, showCount);

  const getColor = (category: string) => categoryColors[category] || '#00FF88';
  const getIcon = (category: string) => categoryIcons[category] || 'ri-news-line';
  const getFlag = (source: string) => {
    for (const key of Object.keys(sourceFlags)) {
      if (source.includes(key)) return sourceFlags[key];
    }
    return '🌐';
  };

  const feat = featuredNews[activeFeatured];

  return (
    <section id="news-timeline" className="relative py-24 md:py-32 overflow-hidden" style={{ background: '#06080F' }}>
      {/* Background */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-0 w-full h-px" style={{ background: 'linear-gradient(90deg, transparent, rgba(255,107,53,0.4), transparent)' }} />
        <div className="absolute top-20 left-10 w-72 h-72 rounded-full opacity-6" style={{ background: 'radial-gradient(circle, rgba(255,107,53,0.15) 0%, transparent 70%)', filter: 'blur(40px)' }} />
        <div className="absolute bottom-20 right-10 w-80 h-80 rounded-full opacity-5" style={{ background: 'radial-gradient(circle, rgba(0,255,136,0.12) 0%, transparent 70%)', filter: 'blur(50px)' }} />
        <div className="absolute inset-0 opacity-3" style={{
          backgroundImage: 'linear-gradient(rgba(255,107,53,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(255,107,53,0.04) 1px, transparent 1px)',
          backgroundSize: '60px 60px',
        }} />
      </div>

      <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-10">
        {/* Header */}
        <div
          ref={headerRef as React.RefObject<HTMLDivElement>}
          className={`text-center mb-14 transition-all duration-700 ${headerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
        >
          <div className="inline-flex items-center gap-3 px-5 py-2 mb-6 rounded-full" style={{ background: 'rgba(255,107,53,0.1)', border: '1px solid rgba(255,107,53,0.3)' }}>
            <span className="w-2 h-2 rounded-full bg-[#FF6B35] animate-pulse" />
            <span className="text-[#FF6B35] text-xs font-bold tracking-widest uppercase" style={{ fontFamily: "'Inter', sans-serif" }}>META NEWS · 元宇宙时讯</span>
            <span className="text-[#FF6B35]/60 text-xs">实时更新</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-black text-white mb-4" style={{ fontFamily: "'Orbitron', monospace" }}>
            元宇宙
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#FF6B35] to-[#FFD700]"> 时讯</span>
          </h2>
          <p className="text-white/40 text-base max-w-2xl mx-auto leading-relaxed" style={{ fontFamily: "'Inter', sans-serif" }}>
            追踪全球元宇宙、6G、量子计算、脑机接口等前沿科技最新动态 · 深度解析 · 按时间倒序排列
          </p>
        </div>

        {/* Featured News Showcase */}
        <div
          ref={featuredRef as React.RefObject<HTMLDivElement>}
          className={`mb-14 transition-all duration-700 delay-100 ${featuredVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
        >
          <div className="flex items-center gap-3 mb-5">
            <div className="w-1 h-6 rounded-full" style={{ background: 'linear-gradient(180deg, #FF6B35, #FFD700)' }} />
            <h3 className="text-white font-bold text-lg" style={{ fontFamily: "'Inter', sans-serif" }}>本周重磅</h3>
            <div className="flex gap-2 ml-auto">
              {featuredNews.map((_, i) => (
                <button key={i} onClick={() => setActiveFeatured(i)}
                  className="w-2 h-2 rounded-full transition-all cursor-pointer"
                  style={{ background: activeFeatured === i ? '#FF6B35' : 'rgba(255,255,255,0.2)', transform: activeFeatured === i ? 'scale(1.4)' : 'scale(1)' }} />
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">
            {/* Main featured */}
            <div className="lg:col-span-3 relative rounded-2xl overflow-hidden cursor-pointer group" style={{ minHeight: '320px', border: `1px solid ${getColor(feat.category)}30` }}>
              <img src={feat.img} alt={feat.title} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700" style={{ minHeight: '320px' }} />
              <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(6,8,15,0.95) 0%, rgba(6,8,15,0.5) 50%, rgba(6,8,15,0.1) 100%)' }} />
              <div className="absolute inset-0 p-6 flex flex-col justify-end">
                <div className="flex items-center gap-2 mb-3">
                  <span className="px-3 py-1 rounded-full text-xs font-bold" style={{ background: `${getColor(feat.category)}20`, color: getColor(feat.category), border: `1px solid ${getColor(feat.category)}40` }}>
                    {feat.category}
                  </span>
                  <span className="px-3 py-1 rounded-full text-xs font-bold" style={{ background: `${feat.impactColor}20`, color: feat.impactColor, border: `1px solid ${feat.impactColor}40` }}>
                    {feat.impact}
                  </span>
                  <span className="text-white/40 text-xs ml-auto">{feat.date}</span>
                </div>
                <h3 className="text-white font-black text-xl leading-snug mb-2" style={{ fontFamily: "'Inter', sans-serif" }}>{feat.title}</h3>
                <p className="text-white/60 text-sm leading-relaxed line-clamp-2" style={{ fontFamily: "'Inter', sans-serif" }}>{feat.summary}</p>
                <div className="flex items-center gap-3 mt-3">
                  <span className="text-white/40 text-xs">{getFlag(feat.source)} {feat.source}</span>
                  <span className="text-white/30 text-xs">· 阅读约{feat.readTime}</span>
                  <div className="flex gap-1 ml-auto">
                    {feat.tags.map(t => (
                      <span key={t} className="text-xs px-2 py-0.5 rounded-full" style={{ background: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.4)' }}>#{t}</span>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Side featured list */}
            <div className="lg:col-span-2 flex flex-col gap-3">
              {featuredNews.map((n, i) => (
                <button key={n.id} onClick={() => setActiveFeatured(i)}
                  className="flex gap-3 p-4 rounded-xl text-left transition-all cursor-pointer group"
                  style={{
                    background: activeFeatured === i ? `${getColor(n.category)}10` : 'rgba(255,255,255,0.02)',
                    border: `1px solid ${activeFeatured === i ? getColor(n.category) + '40' : 'rgba(255,255,255,0.06)'}`,
                  }}>
                  <div className="w-16 h-16 rounded-xl overflow-hidden flex-shrink-0">
                    <img src={n.img} alt={n.title} className="w-full h-full object-cover" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs font-bold" style={{ color: getColor(n.category) }}>{n.category}</span>
                      <span className="text-white/25 text-xs">{n.date}</span>
                    </div>
                    <p className="text-white/80 text-xs font-semibold leading-snug line-clamp-2 group-hover:text-white transition-colors" style={{ fontFamily: "'Inter', sans-serif" }}>{n.title}</p>
                    <span className="text-xs mt-1 inline-block px-2 py-0.5 rounded-full" style={{ background: `${n.impactColor}15`, color: n.impactColor }}>{n.impact}</span>
                  </div>
                </button>
              ))}

              {/* Stats bar */}
              <div className="p-4 rounded-xl mt-auto" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
                <p className="text-white/40 text-xs mb-3 font-semibold" style={{ fontFamily: "'Inter', sans-serif" }}>本月数据概览</p>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { label: '重大突破', value: '12项', color: '#FFD700' },
                    { label: '政策发布', value: '8项', color: '#00FF88' },
                    { label: '融资事件', value: '34起', color: '#FF6B35' },
                    { label: '新品发布', value: '19款', color: '#A855F7' },
                  ].map(s => (
                    <div key={s.label} className="text-center p-2 rounded-lg" style={{ background: `${s.color}08` }}>
                      <div className="font-black text-base" style={{ color: s.color, fontFamily: "'Orbitron', monospace" }}>{s.value}</div>
                      <div className="text-white/30 text-xs">{s.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Category Filter */}
        <div className="flex flex-wrap gap-2 mb-8 justify-center">
          {categories.map((cat) => {
            const color = getColor(cat);
            const icon = getIcon(cat);
            const isActive = activeCategory === cat;
            return (
              <button
                key={cat}
                onClick={() => { setActiveCategory(cat); setShowCount(9); }}
                className="flex items-center gap-1.5 px-4 py-2 text-xs font-semibold transition-all duration-200 cursor-pointer whitespace-nowrap rounded-full"
                style={{
                  border: isActive ? `1.5px solid ${color}` : '1.5px solid rgba(255,255,255,0.1)',
                  color: isActive ? color : 'rgba(255,255,255,0.4)',
                  backgroundColor: isActive ? `${color}12` : 'transparent',
                  boxShadow: isActive ? `0 0 12px ${color}25` : 'none',
                  fontFamily: "'Inter', sans-serif",
                }}
              >
                {cat !== '全部' && <i className={`${icon} text-xs`} />}
                {cat}
              </button>
            );
          })}
        </div>

        {/* Stats bar */}
        <div className="flex items-center justify-between mb-6 px-1">
          <p className="text-white/30 text-xs" style={{ fontFamily: "'Inter', sans-serif" }}>
            共 <span className="text-[#FF6B35] font-bold">{filtered.length}</span> 条时讯
            {activeCategory !== '全部' && <span className="ml-2 text-white/20">· 筛选：{activeCategory}</span>}
          </p>
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-[#FF6B35] animate-pulse" />
            <span className="text-white/30 text-xs" style={{ fontFamily: "'Inter', sans-serif" }}>实时更新</span>
          </div>
        </div>

        {/* News Cards Grid */}
        <div
          ref={gridRef as React.RefObject<HTMLDivElement>}
          className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 transition-all duration-700 ${gridVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
        >
          {displayed.map((news, i) => {
            const color = getColor(news.category);
            const icon = getIcon(news.category);
            const flag = getFlag(news.source);
            const isHovered = hoveredId === String(news.id);

            return (
              <div
                key={news.id}
                className="group relative flex flex-col rounded-2xl overflow-hidden border transition-all duration-300 cursor-pointer hover:-translate-y-1"
                style={{
                  borderColor: isHovered ? `${color}50` : 'rgba(255,255,255,0.07)',
                  background: isHovered
                    ? `linear-gradient(135deg, ${color}08 0%, rgba(255,255,255,0.02) 100%)`
                    : 'rgba(255,255,255,0.02)',
                  transitionDelay: `${(i % 6) * 40}ms`,
                }}
                onMouseEnter={() => setHoveredId(String(news.id))}
                onMouseLeave={() => setHoveredId(null)}
              >
                {/* Top accent bar */}
                <div className="h-0.5 w-full transition-all duration-300" style={{ background: `linear-gradient(90deg, ${color}, ${color}30)`, opacity: isHovered ? 1 : 0.3 }} />

                <div className="flex flex-col flex-1 p-5 gap-3">
                  {/* Header */}
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 flex items-center justify-center rounded-xl flex-shrink-0" style={{ backgroundColor: `${color}15`, border: `1px solid ${color}25` }}>
                        <i className={`${icon} text-sm`} style={{ color }} />
                      </div>
                      <span className="text-xs font-bold px-2 py-0.5 rounded-full" style={{ backgroundColor: `${color}15`, color, fontFamily: "'Inter', sans-serif" }}>
                        {news.category}
                      </span>
                    </div>
                    <span className="text-white/25 text-xs flex-shrink-0 mt-1" style={{ fontFamily: "'Inter', sans-serif" }}>{news.date}</span>
                  </div>

                  {/* Title */}
                  <h4 className="text-white/90 text-sm font-bold leading-snug group-hover:text-white transition-colors duration-200 line-clamp-2" style={{ fontFamily: "'Inter', sans-serif" }}>
                    {news.title}
                  </h4>

                  {/* Summary */}
                  <p className="text-white/40 text-xs leading-relaxed line-clamp-3 flex-1" style={{ fontFamily: "'Inter', sans-serif" }}>{news.summary}</p>

                  {/* Footer */}
                  <div className="flex items-center justify-between pt-2 border-t border-white/5">
                    <div className="flex items-center gap-1.5">
                      <span className="text-sm">{flag}</span>
                      <span className="text-white/30 text-xs" style={{ fontFamily: "'Inter', sans-serif" }}>{news.source}</span>
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {news.tags.slice(0, 2).map((tag) => (
                        <span key={tag} className="text-xs px-1.5 py-0.5 rounded-full border border-white/8 text-white/25" style={{ fontFamily: "'Inter', sans-serif" }}>
                          #{tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Hover glow */}
                {isHovered && (
                  <div className="absolute inset-0 pointer-events-none rounded-2xl" style={{ boxShadow: `inset 0 0 30px ${color}06` }} />
                )}
              </div>
            );
          })}
        </div>

        {/* Load More */}
        {showCount < filtered.length && (
          <div className="flex justify-center mt-10">
            <button
              onClick={() => setShowCount((c) => c + 9)}
              className="flex items-center gap-2 px-8 py-3 text-sm font-semibold transition-all duration-300 cursor-pointer whitespace-nowrap rounded-full hover:scale-105"
              style={{
                border: '1.5px solid rgba(255,107,53,0.3)',
                color: 'rgba(255,107,53,0.8)',
                background: 'rgba(255,107,53,0.06)',
                fontFamily: "'Inter', sans-serif",
              }}
            >
              <i className="ri-arrow-down-line" />
              加载更多时讯 ({filtered.length - showCount} 条)
            </button>
          </div>
        )}

        {/* View All Link */}
        <div className="flex justify-center mt-6">
          <Link to="/news" className="flex items-center gap-2 text-sm font-semibold text-[#FF6B35] hover:gap-3 transition-all duration-300 whitespace-nowrap" style={{ fontFamily: "'Inter', sans-serif" }}>
            <span>查看全部资讯</span>
            <i className="ri-arrow-right-line" />
          </Link>
        </div>
      </div>
    </section>
  );
}
